/**
 * useAlerts Hook
 *
 * Hook to check alerts on page load/navigation.
 * Should be used in the main layout or App component to trigger
 * alert checks when user navigates between pages.
 *
 * @module hooks/useAlerts
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAlertStore } from '../store/alertStore';
import { useAuthStore } from '../store/authStore';

/**
 * Options for the useAlerts hook
 */
interface UseAlertsOptions {
  /** Minimum interval between checks in milliseconds (default: 30000 = 30s) */
  minInterval?: number;
  /** Whether to check on mount (default: true) */
  checkOnMount?: boolean;
  /** Whether to check on route change (default: true) */
  checkOnRouteChange?: boolean;
}

/**
 * Hook to automatically check alerts on page navigation
 *
 * @param options - Configuration options
 * @returns Alert state and actions
 */
export function useAlerts(options: UseAlertsOptions = {}) {
  const {
    minInterval = 30000, // 30 seconds minimum between checks
    checkOnMount = true,
    checkOnRouteChange = true,
  } = options;

  const location = useLocation();
  const lastCheckRef = useRef<number>(0);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const {
    alerts,
    unreadCount,
    alertsLoading,
    alertsError,
    lastChecked,
    checkAlerts,
    markAsRead,
    markAllAsRead,
  } = useAlertStore();

  /**
   * Check alerts if enough time has passed since last check
   */
  const checkAlertsIfNeeded = async () => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;

    // Only check if enough time has passed
    if (timeSinceLastCheck >= minInterval) {
      lastCheckRef.current = now;
      await checkAlerts();
    }
  };

  // Check on mount
  useEffect(() => {
    if (checkOnMount && isAuthenticated) {
      checkAlertsIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Check on route change
  useEffect(() => {
    if (checkOnRouteChange && isAuthenticated) {
      checkAlertsIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return {
    // State
    alerts,
    unreadCount,
    isLoading: alertsLoading,
    error: alertsError,
    lastChecked,

    // Actions
    checkAlerts: checkAlertsIfNeeded,
    forceCheck: checkAlerts, // Bypass minimum interval
    markAsRead,
    markAllAsRead,

    // Computed
    hasUnread: unreadCount > 0,
  };
}

export default useAlerts;
